/**
 * ReportViewer - Read-only view of a report
 *
 * For viewing reports without editing capability.
 * Used for approved reports or when user doesn't have edit permission.
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { useRoleAssumption } from '../../../context/RoleAssumptionContext';
import { getUserProfile, reviseRejectedReport, type ReportWorkflow } from '../../../services/firestore';
import StatusBadge from '../../../components/reports/StatusBadge';
import DeadlineBadge from '../../../components/reports/DeadlineBadge';
import { useRatedInstitutions } from '../../../hooks/useInstitutions';
import '../DraftEditor.css';

const REPORT_SECTIONS = [
  { id: 'executive_summary', titleKey: 'reports.sections.executiveSummary' },
  { id: 'institution_overview', titleKey: 'reports.sections.institutionOverview' },
  { id: 'capital_analysis', titleKey: 'reports.sections.capitalAnalysis' },
  { id: 'asset_quality', titleKey: 'reports.sections.assetQuality' },
  { id: 'management_analysis', titleKey: 'reports.sections.managementAnalysis' },
  { id: 'earnings_analysis', titleKey: 'reports.sections.earningsAnalysis' },
  { id: 'liquidity_analysis', titleKey: 'reports.sections.liquidityAnalysis' },
  { id: 'sensitivity_analysis', titleKey: 'reports.sections.sensitivityAnalysis' },
  { id: 'rating_rationale', titleKey: 'reports.sections.ratingRationale' },
  { id: 'outlook', titleKey: 'reports.sections.outlook' },
];

interface ReportViewerProps {
  report: ReportWorkflow;
}

export default function ReportViewer({ report }: ReportViewerProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { effectiveIsPlatformAdmin, effectiveIsCRAAdmin, effectiveIsCRAAnalyst } = useRoleAssumption();
  const [activeSection, setActiveSection] = useState<string>(REPORT_SECTIONS[0].id);
  const [analystName, setAnalystName] = useState<string>('');
  const [revising, setRevising] = useState(false);

  // Fetch institution data from BigQuery API
  const { institutions } = useRatedInstitutions();

  // Create a lookup map for institution names
  const institutionNameMap = useMemo(() => {
    const map = new Map<string, string>();
    institutions.forEach((inst) => map.set(inst.institution_id, inst.institution_name));
    return map;
  }, [institutions]);

  // Check if user can revise this report
  const canRevise = report.status === 'rejected' && (
    effectiveIsPlatformAdmin ||
    effectiveIsCRAAdmin ||
    (effectiveIsCRAAnalyst && report.analyst_id === userProfile?.uid)
  );

  const handleReviseReport = async () => {
    if (!userProfile?.uid) return;

    try {
      setRevising(true);
      const newReport = await reviseRejectedReport(report.id, userProfile.uid);
      // Navigate to the new draft for editing
      navigate(`/reports/${newReport.id}`);
    } catch (err) {
      console.error('Error revising report:', err);
      alert(err instanceof Error ? err.message : 'Failed to revise report');
    } finally {
      setRevising(false);
    }
  };

  useEffect(() => {
    const loadAnalystName = async () => {
      try {
        const analyst = await getUserProfile(report.analyst_id);
        if (analyst) {
          setAnalystName(analyst.display_name || analyst.email);
        }
      } catch (err) {
        console.error('Error loading analyst name:', err);
      }
    };

    loadAnalystName();
  }, [report.analyst_id]);

  const getInstitutionName = (institutionId: string): string => {
    return institutionNameMap.get(institutionId) || institutionId;
  };

  const formatPeriod = (period: string): string => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const sectionContent = report.content.sections;

  return (
    <div className="draft-editor">
      <div className="draft-header">
        <button className="back-btn" onClick={() => navigate('/reports')}>
          {'← '}{t('common.back')}
        </button>
        <div className="draft-header-info">
          <h1>{getInstitutionName(report.institution_id)}</h1>
          <div className="draft-meta">
            <span className="period">{formatPeriod(report.period)}</span>
            <StatusBadge status={report.status} />
            <DeadlineBadge
              dueDate={report.deadline.final_due_date}
              isCompleted={report.status === 'approved'}
              size="medium"
            />
          </div>
          {analystName && (
            <div className="analyst-info" style={{ marginTop: '8px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              {t('reports.preparedBy')} <strong>{analystName}</strong>
            </div>
          )}
        </div>
      </div>

      {/* Rejection Notice */}
      {report.rejection_reason && (
        <div className="rejection-notice">
          <strong>{t('reports.rejectionReason')}</strong> {report.rejection_reason}
          <span className="rejection-stage">
            ({report.rejection_stage === 'admin' ? t('reports.rejectedByAdmin') : t('reports.rejectedByCommittee')})
          </span>
        </div>
      )}

      {/* Client Comments (if any) */}
      {report.client_comments.length > 0 && (
        <div className="client-comments-section" style={{
          margin: '16px 24px',
          padding: '16px',
          background: 'var(--bg-secondary)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-color)'
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: 600 }}>
            {t('reports.clientCommentsCount', { count: report.client_comments.length })}
          </h3>
          <div className="comments-list">
            {report.client_comments.map((comment) => (
              <div
                key={comment.id}
                style={{
                  padding: '12px',
                  background: 'var(--bg-primary)',
                  borderRadius: 'var(--radius-sm)',
                  marginBottom: '8px',
                  borderLeft: comment.resolved ? '3px solid var(--success)' : '3px solid var(--warning)',
                }}
              >
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  <strong>{comment.author_name}</strong>
                  {comment.section_id && (
                    <span> on {t(REPORT_SECTIONS.find((s) => s.id === comment.section_id)?.titleKey || '')}</span>
                  )}
                  {comment.resolved && (
                    <span style={{ marginLeft: '8px', color: 'var(--success)' }}>{'✓ '}{t('reports.resolved')}</span>
                  )}
                </div>
                <p style={{ margin: 0 }}>{comment.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="draft-content">
        {/* Section Navigation */}
        <div className="section-nav">
          <h3>{t('reports.reportSections')}</h3>
          <ul>
            {REPORT_SECTIONS.map((section) => (
              <li
                key={section.id}
                className={`section-nav-item ${activeSection === section.id ? 'active' : ''} ${sectionContent[section.id]?.content?.trim() ? 'has-content' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                <span className="section-indicator">
                  {sectionContent[section.id]?.content?.trim() ? '●' : '○'}
                </span>
                {t(section.titleKey)}
              </li>
            ))}
          </ul>
        </div>

        {/* Content Viewer */}
        <div className="editor-area">
          <div className="section-editor">
            <h2>{t(REPORT_SECTIONS.find((s) => s.id === activeSection)?.titleKey || '')}</h2>
            <div
              className="section-content-view"
              style={{
                padding: '16px',
                background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-md)',
                minHeight: '300px',
                whiteSpace: 'pre-wrap',
              }}
            >
              {sectionContent[activeSection]?.content || (
                <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  {t('reports.noContent')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status info bar */}
      <div className="submit-bar" style={{ background: 'var(--bg-secondary)' }}>
        <div className="submit-info">
          <span className="sections-complete">
            {t('reports.sectionsComplete', { completed: Object.values(sectionContent).filter((s) => s?.content?.trim()).length, total: REPORT_SECTIONS.length })}
          </span>
        </div>
        {canRevise ? (
          <button
            className="submit-btn"
            onClick={handleReviseReport}
            disabled={revising}
            style={{ background: 'var(--warning)' }}
          >
            {revising ? t('reports.creatingRevision') : t('reports.reviseReport')}
          </button>
        ) : (
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            {t('reports.viewOnly')}
          </div>
        )}
      </div>
    </div>
  );
}
